// Orator — WHAT THIS PERSON MAY SEE, in one place.
//
// A membership carries a `sections` list (0314) naming the areas of a campaign somebody is granted.
// The rule for reading it has one subtlety and everything depends on getting it right:
//
//   AN EMPTY LIST MEANS EVERYTHING THEIR RANK ALLOWS.
//
// That is what every membership held the day the fence shipped, so an undefined or empty list must
// hide NOTHING. Read it as "granted nothing" and the fence ships switched on for every existing
// manager at once -- a dashboard that silently loses half its nav for everybody.
//
// FOUR SCREENS HAD WRITTEN THIS OUT SEPARATELY, which is why it is a module now rather than a line
// you copy: AppShell's `has`, Tags' `allowed`, Home's module-level `has`, and the Users page was
// about to be the fifth when its Timesheets tab needed the same question asked. Same shape as
// lib/viewstate in #1155 -- five hand-rolled copies of twelve lines, one of them quietly wrong for
// months. Nothing had drifted here YET. A rule with an inversion in it, copied five times, is a
// thing to collapse before it drifts rather than after.
//
// NO IMPORTS, deliberately. The Guards CI job runs actions/checkout and nothing else -- no
// setup-node, no node_modules -- so a file a guard reads must stand on its own. That is the lesson
// of lib/viewstate.ts importing react and failing CI in #1155.

/** The shape this reads. Anything carrying a `sections` list will do, which keeps `current` from
 *  the campaign store out of the signature and the file dependency-free. */
export type Granted = { sections?: string[] | null } | null | undefined;

// THE WAY IN AND THE WAY OUT ARE NOT THE SAME RULE, and they look contradictory until you see why.
//
// 0320 made the Users editor REFUSE to save a manager with nothing ticked: a person saved with none
// would sign in to an empty dashboard and have no way to tell that from a bug. So on the way IN,
// empty is an error.
//
// On the way OUT, empty still has to mean everything -- because every membership that predates the
// fence holds exactly that, and no migration invented a list for them. Tightening what can be
// SAVED does not license reinterpreting what is already STORED.
//
// Users.tsx carries the matching note on its validator, and one-grant-rule-guard.py lets those
// lines through as `grant-ok` for this reason.

/** Is `area` granted? An empty or missing list means yes -- see the note above. */
export function grantsArea(who: Granted, area: string): boolean {
  const list = who?.sections;
  if (!list || list.length === 0) return true;
  return list.includes(area);
}

/** Is ANY of `areas` granted? For a page offered on behalf of several sections -- Tags belongs to
 *  whichever of signs/voters/surveys/walkbooks a person holds, rather than being granted itself. */
export function grantsAny(who: Granted, areas: readonly string[]): boolean {
  return areas.some((a) => grantsArea(who, a));
}
